import { onlyKind } from "components/MessageTemplates/TriggerDialogs";
import { Auth0AccountRole, TaskStatus } from "client/openapi";
import {
  aboutRoles,
  actionInfo,
  actionsFor,
  ASSIGN_CHOICES,
  defaultTimingValue,
  defaultTrigger,
  facetsOf,
  isTaskKind,
  kindOf,
  KindTiming,
  kindTiming,
} from "./kinds";
import {
  columnById,
  columnInfo,
  fieldByRef,
  ReferenceData,
  templateById,
} from "./reference";
import type {
  AskFirst,
  AssignTo,
  CrmFieldConfig,
  CrmSubjectsConfig,
  Automation,
  AutomationDraft,
  Gate,
  Position,
  SendTo,
  Step,
  StepAction,
  TaskStep,
  TimePosition,
  Trigger,
  TriggerCard,
  TriggerJoin,
} from "./types";

/*
 * Building and checking automations: good defaults for a new step under a
 * trigger, switching a step's action, keeping steps valid when the trigger
 * changes, the timeline's parts, and the problems a Save should show.
 */

export function uid(prefix: string) {
  return `${prefix}_${Date.now().toString(36)}${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

/** A deep copy (automations are plain data). */
export function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

export const RIGHT_AWAY: TimePosition = { kind: "time", n: 0, unit: "days" };

// ── Defaults ──────────────────────────────────────────────────────────────

export function emptyAssign(): AssignTo {
  return {
    choices: [],
    people: [],
    tutorSubject: null,
    teachSubject: null,
    share: "all",
  };
}

/**
 * Who gets a new task: that task's assignees for a task event, their
 * tutors for an event about a student, else the signed-in admin.
 */
export function defaultAssign(ref: ReferenceData, trigger: Trigger): AssignTo {
  const assign = emptyAssign();
  const roles = aboutRoles(ref, trigger);
  if (isTaskKind(trigger.kind)) assign.choices = ["task_assignees"];
  else if (roles.length === 1 && roles[0] === "student")
    assign.choices = ["tutors"];
  else if (ref.me) assign.people = [ref.me];
  else assign.choices = ["admins"];
  return assign;
}

/** A new step's Ask first: off; when switched on, admins can approve. */
export function defaultAskFirst(): AskFirst {
  return { on: false };
}

/** A task name that fits who the trigger is about, with a variable for their name. */
export function defaultTaskName(ref: ReferenceData, trigger: Trigger) {
  if (isTaskKind(trigger.kind)) return "Follow up on {{task.name}}";
  const role = aboutRoles(ref, trigger)[0] ?? "student";
  return `Follow up with {{${role}.full_name}}`;
}

/** Who a message goes to by default: the student and parents, or the only kind it can reach. */
export function defaultSendTo(ref: ReferenceData, trigger: Trigger): SendTo[] {
  const only = onlyKind(trigger.kind, trigger.options || {}, ref.catalog);
  if (only === Auth0AccountRole.PARENT) return ["parents"];
  if (only === Auth0AccountRole.ORG_TUTOR) return ["tutors"];
  return ["students"];
}

/** Whether a message goes to the organization's admins, instead of the people around its run. */
export function toAdmins(sendTo: SendTo[]) {
  return sendTo.includes("admins");
}

/** The first of the org's own boards with a column for this trigger's people. */
function defaultBoard(ref: ReferenceData, trigger: Trigger) {
  const roles = aboutRoles(ref, trigger);
  return (
    ref.boards.find(
      (b) => !b.builtIn && b.columns.some((c) => roles.includes(c.role))
    ) ??
    ref.boards.find((b) => !b.builtIn) ??
    null
  );
}

/** A new step that does `action`, with good defaults for this trigger. */
export function newStep(
  ref: ReferenceData,
  trigger: Trigger,
  action: StepAction,
  position: Position = RIGHT_AWAY
): Step {
  const base = {
    id: uid("step"),
    position,
    askFirst: defaultAskFirst(),
  };
  switch (action) {
    case "message":
      return {
        ...base,
        action,
        message: {
          templateId: null,
          sendTo: defaultSendTo(ref, trigger),
          sms: false,
          email: true,
        },
      };
    case "task":
      return {
        ...base,
        action,
        task: {
          name: defaultTaskName(ref, trigger),
          description: "",
          due: { n: 1, unit: "days" },
          about: "event",
          assignTo: defaultAssign(ref, trigger),
        },
      };
    case "crm_move":
      return {
        ...base,
        action,
        crmMove: {
          boardId: defaultBoard(ref, trigger)?.id ?? null,
          columnId: null,
        },
      };
    case "crm_field": {
      const roles = aboutRoles(ref, trigger);
      const field = ref.fields.find((f) => roles.includes(f.role)) ?? null;
      return {
        ...base,
        action,
        crmField: { field: field?.field ?? null, value: null },
      };
    }
    case "close_task":
      // The task step is picked where the step sits (smartStep: the one right before).
      return {
        ...base,
        askFirst: { on: actionInfo(ref, action).askFirst },
        action,
        closeTask: { stepId: null, status: TaskStatus.COMPLETE },
      };
    case "assign_tutor":
      // Families see the tutor, so it asks first unless someone turns that off.
      return {
        ...base,
        askFirst: { on: actionInfo(ref, action).askFirst },
        action,
        assignTutor: {
          choose: "all",
          subject: null,
          tutors: [],
          share: "turns",
        },
      };
    case "add_note":
      return {
        ...base,
        askFirst: { on: actionInfo(ref, action).askFirst },
        action,
        note: { text: "", pinned: false },
      };
  }
}

/**
 * The same step doing another action: its id, position, column and Ask
 * first stay. An Ask first still at the old action's default takes the
 * new one's (Assign a tutor asks first by default), keeping who can
 * approve; one someone changed stays as they set it.
 */
export function withAction(
  ref: ReferenceData,
  trigger: Trigger,
  step: Step,
  action: StepAction
): Step {
  if (step.action === action) return step;
  const next = newStep(ref, trigger, action, step.position);
  const untouched = step.askFirst.on === actionInfo(ref, step.action).askFirst;
  const askFirst = untouched
    ? { ...step.askFirst, on: actionInfo(ref, action).askFirst }
    : step.askFirst;
  return { ...next, id: step.id, askFirst, stage: step.stage };
}

/** A new automation on this kind of trigger, off, with no steps yet. */
export function newAutomation(
  ref: ReferenceData,
  kind: string
): AutomationDraft {
  return {
    name: kindOf(ref, kind)?.label ?? "New automation",
    on: false,
    trigger: defaultTrigger(ref, kind),
    steps: [],
  };
}

// ── Keeping steps valid for their trigger ─────────────────────────────────

/**
 * Keeps every step valid after the trigger changes: Assign to choices that
 * can't apply drop off, a message's Who gets it follows the people the
 * trigger can reach (a message to the admins goes on any trigger), an action
 * the trigger can't start becomes a message,
 * the first column's time fits the kind's own timing (behind two or more
 * trigger cards, a delay after them), and each card's own timing fits its
 * kind.
 */
export function fitToTrigger<T extends Lane>(
  ref: ReferenceData,
  automation: T
): T {
  const { trigger } = automation;
  const facets = facetsOf(ref, trigger);
  const allowed = actionsFor(ref, trigger);
  const only = onlyKind(trigger.kind, trigger.options || {}, ref.catalog);
  const first = new Set(
    firstColumn(automation.steps, automation.gates).map((s) => s.id)
  );
  const stacked = entryCards(automation).length > 1;
  const steps = automation.steps.map((original) => {
    let step: Step = original;
    if (!allowed.includes(step.action))
      step = withAction(ref, trigger, step, allowed[0]);
    if (first.has(step.id)) {
      const position = stacked
        ? step.position.before
          ? clone(RIGHT_AWAY)
          : step.position
        : fitFirstTime(ref, trigger, step.position);
      if (position !== step.position) step = { ...step, position };
    }
    if (step.action === "task") {
      const choices = step.task.assignTo.choices.filter((c) =>
        ASSIGN_CHOICES[c].applies(facets)
      );
      if (choices.length !== step.task.assignTo.choices.length)
        step = {
          ...step,
          task: { ...step.task, assignTo: { ...step.task.assignTo, choices } },
        };
    }
    if (step.action === "message" && only && !toAdmins(step.message.sendTo)) {
      const sendTo: SendTo[] =
        only === Auth0AccountRole.PARENT ? ["parents"] : ["tutors"];
      if (
        only === Auth0AccountRole.PARENT ||
        only === Auth0AccountRole.ORG_TUTOR
      )
        step = { ...step, message: { ...step.message, sendTo } };
    }
    return step;
  });
  const fitCards = (cards: TriggerCard[]) =>
    cards.map((card) => ({
      ...card,
      time: cardTime(ref, card.trigger, card.time),
    }));
  return {
    ...automation,
    steps,
    ...(stacked ? { triggers: fitCards(automation.triggers ?? []) } : {}),
    ...(automation.gates
      ? {
          gates: automation.gates.map((gate) => ({
            ...gate,
            cards: fitCards(gate.cards),
          })),
        }
      : {}),
  };
}

// ── The timeline ──────────────────────────────────────────────────────────

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/**
 * A time position in milliseconds: in the first column from the trigger's
 * own moment (negative: before it), in a later one the gap after the
 * column before it.
 */
export function offsetMs(position: Position) {
  if (position.n <= 0) return 0;
  const ms = position.n * (position.unit === "hours" ? HOUR_MS : DAY_MS);
  return position.before ? -ms : ms;
}

// ── A kind's own timing, on its first steps ───────────────────────────────

/** A time in a kind's own unit, signed (negative: before). */
export function timingAmount(timing: KindTiming, position: TimePosition) {
  return offsetMs(position) / (timing.key === "hours" ? HOUR_MS : DAY_MS);
}

/** An amount of a kind's own timing (signed, in its unit) as a time position. */
export function timingPosition(
  timing: KindTiming,
  amount: number
): TimePosition {
  const n = Math.abs(Math.round(amount));
  if (n === 0) return clone(RIGHT_AWAY);
  const side = amount < 0 ? { before: true } : {};
  if (timing.key === "hours" && n % 24 === 0)
    return { kind: "time", n: n / 24, unit: "days", ...side };
  return { kind: "time", n, unit: timing.key, ...side };
}

/** Whether an amount of a kind's own timing is one it allows. */
export function timingAllows(timing: KindTiming, amount: number) {
  if (!Number.isInteger(amount)) return false;
  const size = timing.sides === "before" ? -amount : amount;
  return size >= timing.min && size <= timing.max;
}

/** A new automation's first time: the kind's own default, as the trigger dialog starts it. */
export function defaultFirstPosition(
  ref: ReferenceData,
  trigger: Trigger
): TimePosition {
  const timing = kindTiming(ref, trigger.kind);
  return timing
    ? timingPosition(timing, defaultTimingValue(ref, trigger.kind))
    : clone(RIGHT_AWAY);
}

/** A first column's time that fits the kind's own timing (its side, unit and limits), else its default. */
export function fitFirstTime(
  ref: ReferenceData,
  trigger: Trigger,
  position: TimePosition
): TimePosition {
  const timing = kindTiming(ref, trigger.kind);
  if (!timing) return position.before ? clone(RIGHT_AWAY) : position;
  return timingAllows(timing, timingAmount(timing, position))
    ? position
    : defaultFirstPosition(ref, trigger);
}

/**
 * Moves a kind's own timing out of the trigger's options (where template
 * triggers keep it) onto the first column's steps, so it is set and said
 * in one place: a step at Right away under "3 days after a first meeting"
 * becomes "3 days after".
 */
export function foldTriggerTiming<
  T extends { trigger: Trigger; steps: Step[] }
>(ref: ReferenceData, automation: T): T {
  const timing = kindTiming(ref, automation.trigger.kind);
  const options = { ...(automation.trigger.options || {}) };
  if (!timing || !(timing.key in options)) return automation;
  const raw = Number(options[timing.key]) || 0;
  const value = timing.sides === "before" ? -Math.abs(raw) : raw;
  delete options[timing.key];
  const unit = timing.key === "hours" ? HOUR_MS : DAY_MS;
  const first = new Set(firstColumn(automation.steps).map((s) => s.id));
  return {
    ...automation,
    trigger: { ...automation.trigger, options },
    steps: automation.steps.map((step) =>
      first.has(step.id) && step.position.kind === "time"
        ? {
            ...step,
            position: timingPosition(
              timing,
              value + offsetMs(step.position) / unit
            ),
          }
        : step
    ),
  };
}

/** Whether two positions are the same moment. */
export function samePosition(a: Position, b: Position) {
  return offsetMs(a) === offsetMs(b);
}

/**
 * The steps' columns, left to right, each its steps in order: steps next
 * to each other in one stage, at one time. A step with no stage yet joins
 * the step before it at the same time. The first column counts from the
 * trigger; a later one from when every step in the column before it
 * finished (a gate's own steps, from when it passed).
 */
export function stagesOf(steps: Step[]): Step[][] {
  const columns: Step[][] = [];
  steps.forEach((step, i) => {
    const before = steps[i - 1];
    const joins =
      !!before &&
      samePosition(step.position, before.position) &&
      (step.stage === undefined || step.stage === before.stage);
    if (joins) columns[columns.length - 1].push(step);
    else columns.push([step]);
  });
  return columns;
}

/**
 * The first column's steps, timed from the trigger: none when the lane
 * starts with a gate.
 */
export function firstColumn(steps: Step[], gates: Gate[] = []): Step[] {
  const first = stagesOf(steps)[0] ?? [];
  const stage = first[0]?.stage ?? 0;
  return gates.some((gate) => gate.stage <= stage) ? [] : first;
}

/** A field step that changes the person's subjects, not a custom field. */
export function isSubjects(
  config: CrmFieldConfig
): config is CrmSubjectsConfig {
  return config.field === "subjects" && "subject_ids" in config;
}

/** The task steps before this one (an event position can wait for their task). */
export function earlierTaskSteps(steps: Step[], stepId: string | null) {
  const at =
    stepId === null ? steps.length : steps.findIndex((s) => s.id === stepId);
  return steps
    .slice(0, at < 0 ? steps.length : at)
    .filter((s) => s.action === "task");
}

/** The task step a Close a task step closes, among the lane's steps (null: none picked, or gone). */
export function closedTaskStep(
  steps: Step[],
  stepId: string | null
): TaskStep | null {
  const found = steps.find((s) => s.id === stepId);
  return found?.action === "task" ? found : null;
}

/** Whether two triggers are the same: the same kind, choices and filters. */
export function sameTrigger(a: Trigger, b: Trigger) {
  const key = (t: Trigger) =>
    JSON.stringify([
      t.kind,
      Object.keys(t.options || {})
        .sort()
        .map((k) => [k, t.options[k]]),
      t.filters || [],
    ]);
  return key(a) === key(b);
}

// ── Problems a Save should show ───────────────────────────────────────────

/** Why a step can't run as it is, in plain sentences (empty when it can). */
export function stepProblems(
  ref: ReferenceData,
  automation: { trigger: Trigger; steps: Step[] },
  step: Step
): string[] {
  const problems: string[] = [];
  const roles = aboutRoles(ref, automation.trigger);
  if (!actionsFor(ref, automation.trigger).includes(step.action))
    problems.push(
      `${
        kindOf(ref, automation.trigger.kind)?.label ?? "This trigger"
      } can only send messages.`
    );
  switch (step.action) {
    case "message": {
      const template = templateById(ref, step.message.templateId);
      if (!template) problems.push("Pick a template.");
      else if (!template.has_email && !template.has_sms)
        problems.push("Write the email or the text of this template first.");
      if (!step.message.sms && !step.message.email)
        problems.push("Pick SMS, email or both.");
      if (step.message.sendTo.length === 0) problems.push("Pick who gets it.");
      break;
    }
    case "task": {
      if (!step.task.name.trim()) problems.push("Give the task a name.");
      const a = step.task.assignTo;
      if (a.choices.length === 0 && a.people.length === 0)
        problems.push("Pick who gets the task under Assign to.");
      break;
    }
    case "crm_move": {
      const found = columnById(
        ref,
        step.crmMove.boardId,
        step.crmMove.columnId
      );
      if (!found) {
        problems.push("Pick a board and a column.");
        break;
      }
      if (!roles.includes(found.column.role))
        problems.push(
          `${found.column.name} holds ${
            found.column.role
          }s, and this automation is about ${roleWords(roles)}.`
        );
      const info = columnInfo(ref, found.column.key);
      if (info?.refusal) problems.push(info.refusal);
      else if (
        info &&
        !info.sets &&
        info.needs?.length &&
        !(step.crmMove.values ?? []).some((v) =>
          info.needs?.some((f) => f.field === v.field)
        )
      )
        problems.push(
          `Choose the values that put someone in ${found.column.name}.`
        );
      break;
    }
    case "crm_field": {
      const config = step.crmField;
      if (isSubjects(config)) {
        if (!roles.includes("student") && !roles.includes("tutor"))
          problems.push(
            `Only students and tutors have subjects, and this automation is about ${roleWords(
              roles
            )}.`
          );
        if (!config.subject_ids.length)
          problems.push(
            `Pick the subjects to ${config.mode === "add" ? "add" : "remove"}.`
          );
        break;
      }
      const field = fieldByRef(ref, config.field);
      if (!field) problems.push("Pick a CRM field.");
      else {
        if (!roles.includes(field.role))
          problems.push(
            `${field.label} is a ${
              field.role
            }'s field, and this automation is about ${roleWords(roles)}.`
          );
        const v = config.value;
        if (v === null || v === "" || (Array.isArray(v) && v.length === 0))
          problems.push(`Pick the value to set ${field.label} to.`);
      }
      break;
    }
    case "close_task": {
      const stepId = step.closeTask.stepId;
      const earlier = earlierTaskSteps(automation.steps, step.id);
      if (stepId === null)
        problems.push(
          earlier.length
            ? "Choose the task to close."
            : "Add a Create a task step before this one first."
        );
      else if (!earlier.some((s) => s.id === stepId))
        problems.push("Choose a task from an earlier step to close.");
      break;
    }
    case "assign_tutor": {
      const config = step.assignTutor;
      if (config.choose === "subject" && config.subject === null)
        problems.push("Choose a subject.");
      if (config.choose === "specific" && !config.tutors.length)
        problems.push("Choose the tutors.");
      break;
    }
    case "add_note":
      if (!step.note.text.trim()) problems.push("Write the note.");
      else if (step.note.text.length > NOTE_LIMIT)
        problems.push("Shorten the note to 5,000 characters.");
      break;
  }
  // Assign a tutor and Add a note are for some kinds of people only.
  const people = actionInfo(ref, step.action).people;
  if (people.length && !roles.some((role) => people.includes(role)))
    problems.push(
      `This step is for ${peopleWords(
        people
      )}, and this automation is about ${roleWords(roles)}.`
    );
  // The backend refuses a task step a Close a task step names that is no longer a task.
  if (
    step.action !== "task" &&
    automation.steps.some(
      (s) => s.action === "close_task" && s.closeTask.stepId === step.id
    )
  )
    problems.push(
      "A step closes this step's task. Change that step first, or keep the task."
    );
  return problems;
}

/** The longest note Add a note takes. */
export const NOTE_LIMIT = 5000;

/** "students and parents". */
function peopleWords(people: string[]) {
  const words = people.map((p) => `${p}s`);
  return words.length <= 1
    ? words[0] ?? "nobody"
    : `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

function roleWords(roles: string[]) {
  const words = roles.map((r) => `${r}s`);
  return words.length <= 1
    ? words[0] ?? "nobody"
    : `${words.slice(0, -1).join(", ")} or ${words[words.length - 1]}`;
}

/** Every step's and gate's problems, for a Save: [] when it can be saved. */
export function automationProblems(ref: ReferenceData, automation: Lane) {
  return [
    ...automation.steps.flatMap((step) =>
      stepProblems(ref, automation, step).map((text) => ({
        stepId: step.id,
        text,
      }))
    ),
    ...(automation.gates ?? []).flatMap((gate) =>
      gateProblems(ref, automation, gate).map((text) => ({
        stepId: gate.id,
        text,
      }))
    ),
  ];
}

/** A read automation as a draft to edit (and Save through updateAutomation). */
export function draftOf(automation: Automation): AutomationDraft {
  return clone({
    name: automation.name,
    on: automation.on,
    trigger: automation.trigger,
    triggers: automation.triggers,
    logic: automation.logic,
    steps: automation.steps,
    gates: automation.gates,
  });
}

// ── The entry and gates ───────────────────────────────────────────────────

/** An automation as its lane: the entry, the steps, and the gates between them. */
export interface Lane {
  trigger: Trigger;
  triggers?: TriggerCard[];
  logic?: TriggerJoin;
  steps: Step[];
  gates?: Gate[];
}

/** The option a gate's task card names its lane's task step by. */
export const TASK_STEP = "step";

/** Whether a gate's card waits for a task an earlier step of its lane makes. */
export function isLaneTask(trigger: Trigger) {
  return isTaskKind(trigger.kind) && TASK_STEP in (trigger.options ?? {});
}

/** A gate's card for a task an earlier step of its lane makes: that step's id, else null. */
export function cardTaskStep(trigger: Trigger): string | null {
  if (!isLaneTask(trigger)) return null;
  const step = trigger.options?.[TASK_STEP];
  return step === undefined || step === null || step === ""
    ? null
    : String(step);
}

/**
 * Whether a stack's cards are an AND box of two or more, where a kind that
 * says where someone is goes by its own words (boxCardWords, pickerKinds).
 */
export function inAndBox(logic: TriggerJoin | undefined, cards: number) {
  return cards > 1 && logic !== "or";
}

/** The entry's cards: two or more, each with its own timing; else the trigger alone, its timing on the first column. */
export function entryCards(automation: {
  trigger: Trigger;
  triggers?: TriggerCard[];
}): TriggerCard[] {
  return automation.triggers && automation.triggers.length > 1
    ? automation.triggers
    : [{ id: null, trigger: automation.trigger, time: null }];
}

/** A card's own timing, fit to its kind (its default when it has none yet); null for a kind without one. */
export function cardTime(
  ref: ReferenceData,
  trigger: Trigger,
  time: TimePosition | null | undefined
): TimePosition | null {
  if (!kindTiming(ref, trigger.kind)) return null;
  return time
    ? fitFirstTime(ref, trigger, time)
    : defaultFirstPosition(ref, trigger);
}

/** A new card on this trigger, with its kind's own timing. */
export function newCard(ref: ReferenceData, trigger: Trigger): TriggerCard {
  return { id: null, trigger, time: cardTime(ref, trigger, null) };
}

/**
 * The lane with this entry: its cards and how they join. From one card to
 * two, the first column's time (the kind's own) moves onto that card, and
 * the column follows the stack right away; from two back to one, the
 * card's own time and that delay are the column's again.
 */
export function withEntry<T extends Lane>(
  ref: ReferenceData,
  automation: T,
  cards: TriggerCard[],
  logic: TriggerJoin
): T {
  const before = entryCards(automation);
  const first = new Set(
    firstColumn(automation.steps, automation.gates).map((s) => s.id)
  );
  const firstTime = automation.steps.find((s) => first.has(s.id))?.position;
  const retimed = (position: TimePosition) =>
    automation.steps.map((s) =>
      first.has(s.id) ? { ...s, position: clone(position) } : s
    );
  if (cards.length > 1) {
    let next = cards.map((card) => ({
      ...card,
      time: cardTime(ref, card.trigger, card.time),
    }));
    let steps = automation.steps;
    const timing = kindTiming(ref, before[0].trigger.kind);
    if (before.length === 1 && timing && firstTime) {
      next = next.map((card, i) =>
        i === 0
          ? { ...card, time: cardTime(ref, card.trigger, firstTime) }
          : card
      );
      steps = retimed(RIGHT_AWAY);
    }
    return {
      ...automation,
      trigger: next[0].trigger,
      triggers: next,
      logic,
      steps,
    };
  }
  const card = cards[0];
  let steps = automation.steps;
  const timing = kindTiming(ref, card.trigger.kind);
  if (before.length > 1 && timing && firstTime) {
    const own = card.time ?? defaultFirstPosition(ref, card.trigger);
    const unit = timing.key === "hours" ? HOUR_MS : DAY_MS;
    const amount = timingAmount(timing, own) + offsetMs(firstTime) / unit;
    steps = retimed(
      fitFirstTime(ref, card.trigger, timingPosition(timing, amount))
    );
  }
  return {
    ...automation,
    trigger: card.trigger,
    triggers: [],
    logic: "and",
    steps,
  };
}

/** Why a gate can't wait as it is, in plain sentences (empty when it can). */
export function gateProblems(
  ref: ReferenceData,
  automation: Lane,
  gate: Gate
): string[] {
  const earlier = automation.steps.filter(
    (s) => s.action === "task" && (s.stage ?? 0) < gate.stage
  );
  return gate.cards.flatMap((card) => {
    const { trigger } = card;
    const stepId = cardTaskStep(trigger);
    if (isLaneTask(trigger)) {
      if (stepId === null) return ["Choose the task it waits for."];
      return earlier.some((s) => s.id === stepId)
        ? []
        : [
            "It waits for a task step that is not before it any more. Choose another.",
          ];
    }
    if (!kindOf(ref, trigger.kind)) return ["Choose what it waits for."];
    if (trigger.kind === "column_joined" && !trigger.options?.column)
      return ["Choose the CRM column it waits for."];
    return [];
  });
}
